/**
 * 자동 배치 — 토폴로지 뷰 디자인(2026-10, 전사_토폴로지 뷰 PNG).
 *
 * - 설비(source) 노드는 원형이고, 자기 공장 수집기 둘레에 **동심원(링)** 으로 둘러 배치한다.
 *   한 링에 들어가는 개수는 둘레/간격으로 정하고, 넘치면 다음 링으로 나간다(52대 → 3링).
 * - 수집기·Outbox·Uploader·저장 테이블·출력은 둥근 사각 노드이고 dagre(rankdir LR) 로 좌→우 배치한다.
 *   이때 수집기는 링 지름만큼의 큰 노드로 넣어 주변 노드와 겹치지 않게 한다.
 * - dagre 실패 시(예외) 컬럼별 명시 좌표 폴백.
 *
 * React Flow 좌표는 노드 박스의 좌상단이다. 노드 박스는 도형(원/사각) 아래에 라벨이 붙는 형태라
 * 도형 중심이 박스 중심과 다르다 → 아래 *_CENTER 오프셋으로 변환한다.
 */
import dagre from "@dagrejs/dagre";
import { FACTORIES } from "../data/machines";
import { nodeKind, parseMachineNodeId } from "./node-ids";

/* 설비(원형) 노드 박스: 지름 40 원 + 아래 라벨 */
export const MACHINE_W = 76;
export const MACHINE_H = 66;
export const MACHINE_CENTER = { x: MACHINE_W / 2, y: 22 };
/* 처리기·저장소·출력(둥근 사각) 노드 박스: 60 사각 + 아래 두 줄 라벨 */
export const BOX_W = 160;
export const BOX_H = 118;
export const BOX_CENTER = { x: BOX_W / 2, y: 34 };

/* 이전 버전과의 호환(포커스 이동 등에서 쓰던 이름) — 사각 노드 기준 */
export const NODE_WIDTH = BOX_W;
export const NODE_HEIGHT = BOX_H;

/** 노드 종류별 박스 크기와 도형 중심 오프셋 */
export function nodeGeometry(id) {
  return nodeKind(id) === "machine"
    ? { w: MACHINE_W, h: MACHINE_H, c: MACHINE_CENTER }
    : { w: BOX_W, h: BOX_H, c: BOX_CENTER };
}

const DEFAULT_OPTIONS = {
  nodesep: 18,
  ranksep: 90,
  ringStart: 100,   // 첫 링 반지름 (수집기 중심 → 설비 원 중심) — 사각 노드 라벨과 겹치지 않는 최소 거리
  ringStep: 70,     // 링 간격
  ringSpacing: 60,  // 같은 링에서 설비 사이 호 길이 (원 40 + 라벨 여백) → 52대가 3링(10·17·25)에 들어간다
};

function factoryOrder() {
  return Object.fromEntries(FACTORIES.map((f, i) => [f.id, i]));
}

function machineNo(id) {
  const m = parseMachineNodeId(id);
  return m ? Number(m.machineId.slice(1)) : Number.MAX_SAFE_INTEGER;
}

/** 공장별 설비 id (호기 순) */
function machinesByFactory(nodes) {
  const map = new Map();
  for (const n of nodes) {
    if (nodeKind(n.id) !== "machine") continue;
    const f = parseMachineNodeId(n.id)?.factoryId ?? n.data.factoryId ?? "unknown";
    (map.get(f) ?? map.set(f, []).get(f)).push(n.id);
  }
  for (const ids of map.values()) ids.sort((a, b) => machineNo(a) - machineNo(b));
  return map;
}

/** n 대를 링에 나눠 담는다 → [{radius, count}] */
function ringsFor(n, opt) {
  const rings = [];
  let left = n;
  let r = opt.ringStart;
  while (left > 0) {
    const cap = Math.max(6, Math.floor((2 * Math.PI * r) / opt.ringSpacing));
    const count = Math.min(cap, left);
    rings.push({ radius: r, count });
    left -= count;
    r += opt.ringStep;
  }
  return rings;
}

/** 수집기 중심(cx, cy) 둘레에 설비 좌표(박스 좌상단)를 채운다. 12시 방향부터 시계 방향 */
function placeRing(ids, cx, cy, opt, out) {
  const rings = ringsFor(ids.length, opt);
  let i = 0;
  for (const ring of rings) {
    for (let k = 0; k < ring.count; k += 1, i += 1) {
      // 바깥 링은 반 칸 돌려 안쪽 링과 엇갈리게 한다
      const offset = rings.indexOf(ring) % 2 === 1 ? 0.5 : 0;
      const a = -Math.PI / 2 + ((k + offset) / ring.count) * Math.PI * 2;
      const x = cx + ring.radius * Math.cos(a);
      const y = cy + ring.radius * Math.sin(a);
      out[ids[i]] = { x: Math.round(x - MACHINE_CENTER.x), y: Math.round(y - MACHINE_CENTER.y) };
    }
  }
}

function ringDiameter(count, opt) {
  if (count === 0) return BOX_W;
  const rings = ringsFor(count, opt);
  return (rings[rings.length - 1].radius + MACHINE_W) * 2;
}

/** 모든 노드의 좌표(박스 좌상단)를 계산해 반환한다. 입력 노드는 변경하지 않는다. */
export function layoutPipeline(nodes, edges, options = {}) {
  if (nodes.length === 0) return {};
  const opt = { ...DEFAULT_OPTIONS, ...options };
  try {
    return dagreLayout(nodes, edges, opt);
  } catch (err) {
    console.warn("[layout-pipeline] dagre 실패 → 명시 좌표 폴백", err);
    return fallbackLayout(nodes, opt);
  }
}

function dagreLayout(nodes, edges, opt) {
  const byFactory = machinesByFactory(nodes);
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: opt.nodesep, ranksep: opt.ranksep, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));

  const ids = new Set();
  for (const n of nodes) {
    if (nodeKind(n.id) === "machine") continue;
    ids.add(n.id);
    if (nodeKind(n.id) === "collector") {
      const d = ringDiameter(byFactory.get(n.data.factoryId)?.length ?? 0, opt);
      g.setNode(n.id, { width: d, height: d });
    } else {
      g.setNode(n.id, { width: BOX_W, height: BOX_H });
    }
  }
  const seen = new Set();
  for (const e of edges) {
    if (!ids.has(e.source) || !ids.has(e.target)) continue;
    const key = `${e.source}->${e.target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    g.setEdge(e.source, e.target);
  }
  dagre.layout(g);

  const positions = {};
  for (const n of nodes) {
    if (!ids.has(n.id)) continue;
    const p = g.node(n.id);
    if (!p) continue;
    // dagre 는 중심 좌표 → 도형 중심을 그 자리에 둔다
    positions[n.id] = { x: Math.round(p.x - BOX_CENTER.x), y: Math.round(p.y - BOX_CENTER.y) };
    if (nodeKind(n.id) === "collector") {
      placeRing(byFactory.get(n.data.factoryId) ?? [], p.x, p.y, opt, positions);
    }
  }
  // 수집기가 없는 공장의 설비(목록 불일치)는 왼쪽 위에 링으로 모아 둔다
  let fy = 40;
  for (const [factoryId, list] of byFactory) {
    if (list.every((id) => positions[id])) continue;
    const d = ringDiameter(list.length, opt);
    placeRing(list, d / 2, fy + d / 2, opt, positions);
    fy += d + 40;
    console.warn(`[layout-pipeline] ${factoryId} 수집기 노드 없음 — 설비만 따로 배치`);
  }
  return positions;
}

/** dagre 없이: 공장마다 링을 세로로 쌓고, 나머지는 단계별 컬럼으로 */
function fallbackLayout(nodes, opt) {
  const positions = {};
  const byFactory = machinesByFactory(nodes);
  const order = factoryOrder();
  const collectors = nodes.filter((n) => nodeKind(n.id) === "collector")
    .sort((a, b) => (order[a.data.factoryId] ?? 99) - (order[b.data.factoryId] ?? 99));
  let y = 40;
  let maxD = BOX_W;
  for (const c of collectors) {
    const d = ringDiameter(byFactory.get(c.data.factoryId)?.length ?? 0, opt);
    maxD = Math.max(maxD, d);
    const cx = 40 + d / 2;
    const cy = y + d / 2;
    positions[c.id] = { x: Math.round(cx - BOX_CENTER.x), y: Math.round(cy - BOX_CENTER.y) };
    placeRing(byFactory.get(c.data.factoryId) ?? [], cx, cy, opt, positions);
    y += d + 40;
  }
  const rankOf = { outbox: 1, uploader: 2, table: 3, legacy: 3, output: 4 };
  const byRank = new Map();
  for (const n of nodes) {
    if (positions[n.id] || nodeKind(n.id) === "machine") continue;
    const rank = rankOf[nodeKind(n.id)] ?? 4;
    (byRank.get(rank) ?? byRank.set(rank, []).get(rank)).push(n);
  }
  for (const [rank, list] of byRank) {
    list.forEach((n, i) => {
      positions[n.id] = { x: Math.round(40 + maxD + rank * (BOX_W + opt.ranksep)), y: Math.round(40 + i * (BOX_H + opt.nodesep)) };
    });
  }
  return positions;
}

/** position 이 비어 있는 노드가 하나라도 있으면 true (초기 레이아웃 필요 여부) */
export function needsLayout(nodes) {
  return nodes.some((n) => !n.position);
}
